import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useContracts, useClients, useTarifas, useComisionConfig, useConvenios } from "../../contexts/DataContext";
import { Heading } from "../../components/heading";
import { Text } from "../../components/text";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/table";
import { Badge } from "../../components/badge";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/20/solid";
import {
  Treemap,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { FianzaView } from "../../components/FianzaView";
import {
  getFechaEmision,
  getEmisorNombre,
  getSubRamo,
  getTipoFianza,
  formatNumeroFianza,
} from "../../utils/calculations";
import { useComisionLedger } from "../../hooks/useComisionLedger";
import { indexMovementsByFianza, resolveComision } from "../../utils/comisionReconcile";

const monthNames = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

// Colors for treemap cells
const TREEMAP_COLORS = [
  '#284865', // azul-surelia
  '#3b6a8a', // lighter blue
  '#4d8bb0', // even lighter
  '#60add6', // light blue
  '#00D4FF', // cian-digital
  '#14b8a6', // teal
  '#22c55e', // green
  '#84cc16', // lime
  '#C2F40C', // verde-lima
  '#eab308', // yellow
  '#f59e0b', // amber
  '#f97316', // orange
];

const getTreemapColor = (index) => {
  return TREEMAP_COLORS[index % TREEMAP_COLORS.length];
};

// Custom treemap cell that only shows label if cell is big enough
const CustomTreemapContent = (props) => {
  const { x, y, width, height, name, value, index } = props;

  // Only show text if cell is large enough
  const showName = width > 80 && height > 40;
  const showValue = width > 60 && height > 30;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={getTreemapColor(index)}
        stroke="#fff"
        strokeWidth={2}
        rx={4}
        className="transition-opacity hover:opacity-80"
      />
      {showName && (
        <text
          x={x + width / 2}
          y={y + height / 2 - (showValue ? 8 : 0)}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#fff"
          fontSize={width > 120 ? 12 : 10}
          fontWeight="600"
          className="pointer-events-none"
        >
          {name.length > 20 ? name.substring(0, 18) + "…" : name}
        </text>
      )}
      {showValue && showName && (
        <text
          x={x + width / 2}
          y={y + height / 2 + 12}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="rgba(255,255,255,0.85)"
          fontSize={10}
          className="pointer-events-none"
        >
          {new Intl.NumberFormat("es-MX", {
            style: "currency",
            currency: "MXN",
            notation: "compact",
            maximumFractionDigits: 1,
          }).format(value)}
        </text>
      )}
    </g>
  );
};

export function ProduccionPorCliente({ selectedYear }) {
  const { contractsList, contractsLoading } = useContracts();
  const { clientsById, clientsLoading } = useClients();
  const { tarifas, tarifasLoading } = useTarifas();
  const { movements } = useComisionLedger();
  const { config: comisionConfig } = useComisionConfig();
  const { conveniosById } = useConvenios();
  const byFianza = useMemo(() => indexMovementsByFianza(movements), [movements]);

  const [selectedMonth, setSelectedMonth] = useState("all");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [slideOverData, setSlideOverData] = useState(null); // { clientId, monthIndex }
  const [selectedFianza, setSelectedFianza] = useState(null);

  // Build fianzas with all needed data
  const allFianzas = useMemo(() => {
    return contractsList.flatMap((contract) => {
      // Convenio-split context so the on-the-fly fallback nets the same way
      // the ledger books (see comisionReconcile.resolveComision).
      const splitOpts = {
        contractData: contract,
        orgConfig: comisionConfig,
        convenioData: contract.convenioId ? conveniosById?.[contract.convenioId] : null,
      };
      // Los contratos de convenio no tienen clientId: sin una llave propia
      // todos caían en la misma fila fantasma "Cliente sin nombre". El convenio
      // es su titular, así que agrupan por convenio.
      const client = clientsById[contract.clientId];
      const clientName = contract.convenioId
        ? conveniosById?.[contract.convenioId]?.nombre || "Convenio sin nombre"
        : client?.nombreEmpresa || "Cliente sin nombre";
      const grupoId = contract.clientId
        || (contract.convenioId ? `conv:${contract.convenioId}` : "sin-cliente");

      return (contract.fianzas || [])
        .filter((fianza) => fianza.estadoFianza !== 'anulada' && fianza.estadoFianza !== 'sustituida') // anulada + sustituida excluded; cancelada retained
        .map((fianza) => {
        const inicio = getFechaEmision(fianza);
        const montoAfianzado = Number(fianza.montoAfianzado) || 0;
        const emisorNombre = getEmisorNombre(fianza);
        const fianzaSubRamo = getSubRamo(fianza);
        const fianzaTipo = getTipoFianza(fianza);

        // Ledger-net prima AND comisión when the fianza has movements; on-the-fly
        // otherwise — so the prima column matches the comisión beside it.
        const {
          prima: primaAmount,
          comisionPct,
          comisionMonto,
          primaMinima,
          tarifa: fianzaTarifa,
        } = resolveComision(
          fianza, byFianza.get(fianza.id), tarifas, emisorNombre, fianzaSubRamo, fianzaTipo, splitOpts
        );

        return {
          ...fianza,
          clientId: contract.clientId,
          grupoId,
          contratoId: contract.id,
          clientName,
          inicioVigenciaMs: inicio,
          montoAfianzado,
          primaAmount,
          fianzaTarifa,
          primaMinima,
          comisionPct,
          comisionMonto,
        };
      });
    });
  }, [contractsList, clientsById, tarifas, byFianza, comisionConfig, conveniosById]);

  // Filter fianzas by period
  const filteredFianzas = useMemo(() => {
    return allFianzas.filter((f) => {
      if (!f.inicioVigenciaMs) return false;
      const date = new Date(f.inicioVigenciaMs);
      const yr = date.getFullYear();
      const month = date.getMonth();
      if (yr !== selectedYear) return false;
      if (selectedMonth !== "all" && month !== selectedMonth) return false;
      return true;
    });
  }, [allFianzas, selectedYear, selectedMonth]);

  // Aggregate by client
  const resumenPorCliente = useMemo(() => {
    const map = new Map();

    filteredFianzas.forEach((fianza) => {
      let entry = map.get(fianza.grupoId);
      if (!entry) {
        entry = {
          id: fianza.grupoId,
          name: fianza.clientName,
          contratosSet: new Set(),
          fianzasCount: 0,
          prima: 0,
          comision: 0,
        };
        map.set(fianza.grupoId, entry);
      }
      entry.contratosSet.add(fianza.contratoId);
      entry.fianzasCount += 1;
      entry.prima += fianza.primaAmount || 0;
      entry.comision += fianza.comisionMonto || 0;
    });

    return Array.from(map.values())
      .map((entry) => ({
        ...entry,
        contratos: entry.contratosSet.size,
      }))
      .sort((a, b) => b.prima - a.prima);
  }, [filteredFianzas]);

  // Monthly breakdown by client (for the year)
  const monthlyByCliente = useMemo(() => {
    const yearFianzas = allFianzas.filter((f) => {
      if (!f.inicioVigenciaMs) return false;
      return new Date(f.inicioVigenciaMs).getFullYear() === selectedYear;
    });

    const matrix = new Map();

    yearFianzas.forEach((fianza) => {
      const month = new Date(fianza.inicioVigenciaMs).getMonth();

      let entry = matrix.get(fianza.grupoId);
      if (!entry) {
        entry = {
          id: fianza.grupoId,
          name: fianza.clientName,
          months: Array.from({ length: 12 }, () => ({
            prima: 0,
            comision: 0,
            count: 0,
            contratosSet: new Set(),
          })),
          total: { prima: 0, comision: 0, count: 0 },
        };
        matrix.set(fianza.grupoId, entry);
      }

      entry.months[month].prima += fianza.primaAmount || 0;
      entry.months[month].comision += fianza.comisionMonto || 0;
      entry.months[month].count += 1;
      entry.months[month].contratosSet.add(fianza.contratoId);
      entry.total.prima += fianza.primaAmount || 0;
      entry.total.comision += fianza.comisionMonto || 0;
      entry.total.count += 1;
    });

    return Array.from(matrix.values())
      .map((entry) => ({
        ...entry,
        months: entry.months.map((m) => ({
          prima: m.prima,
          comision: m.comision,
          count: m.count,
          contratos: m.contratosSet.size,
        })),
      }))
      .sort((a, b) => b.total.prima - a.total.prima);
  }, [allFianzas, selectedYear]);

  // Treemap data
  const treemapData = useMemo(() => {
    return resumenPorCliente.map((client) => ({
      name: client.name,
      value: client.prima,
      comision: client.comision,
      fianzas: client.fianzasCount,
    }));
  }, [resumenPorCliente]);

  // Get monthly data for a specific client
  const getMonthlyDataForCliente = (clientId) => {
    return monthlyByCliente.find((c) => c.id === clientId)?.months || [];
  };

  // Get fianzas for a specific client and month
  const getFianzasForClienteMonth = (clientId, monthIndex) => {
    return allFianzas.filter((f) => {
      if (!f.inicioVigenciaMs) return false;
      const date = new Date(f.inicioVigenciaMs);
      if (date.getFullYear() !== selectedYear) return false;
      if (date.getMonth() !== monthIndex) return false;
      return f.grupoId === clientId;
    });
  };

  // Get client and contract data for FianzaView
  const getClientDataForFianza = (fianza) => {
    return clientsById[fianza.clientId] || {};
  };

  const getContractDataForFianza = (fianza) => {
    return contractsList.find((c) => c.id === fianza.contratoId) || {};
  };

  const toggleRowExpanded = (id) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const loading = contractsLoading || clientsLoading || tarifasLoading;

  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(value);

  // Totals
  const totals = useMemo(() => {
    return resumenPorCliente.reduce(
      (acc, row) => ({
        contratos: acc.contratos + row.contratos,
        fianzas: acc.fianzas + row.fianzasCount,
        prima: acc.prima + row.prima,
        comision: acc.comision + row.comision,
      }),
      { contratos: 0, fianzas: 0, prima: 0, comision: 0 }
    );
  }, [resumenPorCliente]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Heading level={1} className="text-3xl font-bold text-azul-surelia">
            Producción por Cliente
          </Heading>
          <Badge color="blue">{selectedYear}</Badge>
        </div>
        <Text className="text-azul-surelia/70">
          Producción y comisiones estimadas por cliente en el periodo seleccionado.
        </Text>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center" data-pdf-hide>
        <div className="flex items-center gap-2">
          <Text className="text-sm text-azul-surelia/80">Mes</Text>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value === "all" ? "all" : Number(e.target.value))}
            className="text-sm border rounded-lg px-3 py-2"
          >
            <option value="all">Todo el año</option>
            {monthNames.map((name, idx) => (
              <option key={name} value={idx}>
                {name.charAt(0).toUpperCase() + name.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Summary badges */}
        <div className="flex gap-2 ml-auto">
          <Badge color="zinc">{resumenPorCliente.length} clientes</Badge>
          <Badge color="blue">{totals.fianzas} fianzas</Badge>
        </div>
      </div>

      {/* Treemap Chart */}
      {!loading && treemapData.length > 0 && (
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-zinc-200">
            <Heading level={3} className="text-xl text-azul-surelia">
              Distribución de producción
            </Heading>
            <Text className="text-sm text-azul-surelia/60 mt-1">
              Prima por cliente - {selectedMonth === "all" ? "año completo" : monthNames[selectedMonth]} {selectedYear}
            </Text>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={350}>
              <Treemap
                data={treemapData}
                dataKey="value"
                aspectRatio={4 / 3}
                stroke="#fff"
                content={<CustomTreemapContent />}
              >
                <Tooltip
                  content={({ payload }) => {
                    if (!payload || !payload.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white border border-zinc-200 rounded-lg shadow-lg p-3">
                        <p className="font-semibold text-azul-surelia text-sm mb-2">{data.name}</p>
                        <div className="space-y-1 text-xs">
                          <p className="flex justify-between gap-4">
                            <span className="text-zinc-500">Prima:</span>
                            <span className="font-semibold">{formatCurrency(data.value)}</span>
                          </p>
                          <p className="flex justify-between gap-4">
                            <span className="text-zinc-500">Comisión:</span>
                            <span className="font-semibold text-azul-surelia">{formatCurrency(data.comision)}</span>
                          </p>
                          <p className="flex justify-between gap-4">
                            <span className="text-zinc-500">Fianzas:</span>
                            <span className="font-semibold">{data.fianzas}</span>
                          </p>
                        </div>
                      </div>
                    );
                  }}
                />
              </Treemap>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Summary Table */}
      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
          <Heading level={3} className="text-xl text-azul-surelia">
            Resumen por cliente
          </Heading>
          <Badge color="zinc">
            Período: {selectedMonth === "all" ? "Año completo" : monthNames[selectedMonth]} {selectedYear}
          </Badge>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHead className="bg-azul-surelia text-white">
              <TableRow>
                <TableHeader className="text-white w-8"></TableHeader>
                <TableHeader className="text-white">Cliente</TableHeader>
                <TableHeader className="text-white text-center">Contratos</TableHeader>
                <TableHeader className="text-white text-center">Fianzas</TableHeader>
                <TableHeader className="text-white text-right">Prima</TableHeader>
                <TableHeader className="text-white text-right">Comisión</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan="6" className="py-8 text-center text-azul-surelia/70">
                    Cargando datos…
                  </TableCell>
                </TableRow>
              ) : resumenPorCliente.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="6" className="py-8 text-center text-azul-surelia/70">
                    No hay datos para este periodo.
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {resumenPorCliente.map((row, index) => {
                    const isExpanded = expandedRows.has(row.id);
                    const monthlyData = getMonthlyDataForCliente(row.id);

                    return (
                      <React.Fragment key={row.id}>
                        <TableRow
                          className={`transition-colors hover:bg-azul-surelia/5 cursor-pointer ${
                            index % 2 === 0 ? "bg-white" : "bg-zinc-50/50"
                          } ${isExpanded ? "bg-azul-surelia/5" : ""}`}
                          onClick={() => toggleRowExpanded(row.id)}
                        >
                          <TableCell className="py-3 w-8">
                            <ChevronDownIcon
                              className={`w-5 h-5 text-azul-surelia/60 transition-transform ${
                                isExpanded ? "rotate-0" : "-rotate-90"
                              }`}
                            />
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="font-medium text-zinc-900">{row.name}</span>
                          </TableCell>
                          <TableCell className="text-center py-3 text-zinc-700">{row.contratos}</TableCell>
                          <TableCell className="text-center py-3 text-zinc-700">{row.fianzasCount}</TableCell>
                          <TableCell className="text-right py-3 font-semibold text-zinc-900">
                            {formatCurrency(row.prima)}
                          </TableCell>
                          <TableCell className="text-right py-3 font-semibold text-azul-surelia">
                            {formatCurrency(row.comision)}
                          </TableCell>
                        </TableRow>
                        {/* Expanded monthly breakdown */}
                        {isExpanded && monthlyData.map((monthData, monthIndex) => {
                          if (monthData.prima === 0 && monthData.count === 0) return null;
                          const isActiveRow = slideOverData?.clientId === row.id && slideOverData?.monthIndex === monthIndex;
                          return (
                            <TableRow
                              key={`${row.id}-month-${monthIndex}`}
                              className={`cursor-pointer transition-colors ${
                                isActiveRow
                                  ? "bg-azul-surelia/10 border-l-4 border-l-azul-surelia"
                                  : "bg-zinc-50/80 hover:bg-azul-surelia/5"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (slideOverData?.clientId === row.id && slideOverData?.monthIndex === monthIndex) {
                                  setSlideOverData(null);
                                } else {
                                  setSlideOverData({ clientId: row.id, clientName: row.name, monthIndex });
                                }
                              }}
                            >
                              <TableCell className="py-2 w-8"></TableCell>
                              <TableCell className={`py-2 pl-10 ${isActiveRow ? "pl-9" : ""}`}>
                                <span className={`text-sm ${isActiveRow ? "text-azul-surelia font-medium" : "text-azul-surelia/70 hover:text-azul-surelia hover:underline"}`}>
                                  {monthNames[monthIndex].charAt(0).toUpperCase() + monthNames[monthIndex].slice(1)}
                                </span>
                              </TableCell>
                              <TableCell className="text-center py-2 text-zinc-600">
                                {monthData.contratos}
                              </TableCell>
                              <TableCell className="text-center py-2 text-zinc-600">
                                {monthData.count}
                              </TableCell>
                              <TableCell className="text-right py-2 text-zinc-700">
                                {formatCurrency(monthData.prima)}
                              </TableCell>
                              <TableCell className="text-right py-2 text-zinc-700">
                                {formatCurrency(monthData.comision)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  {/* Totals row */}
                  <TableRow className="bg-azul-surelia/5 border-t-2 border-azul-surelia/20">
                    <TableCell className="py-3 w-8"></TableCell>
                    <TableCell className="py-3 font-semibold text-azul-surelia">Total</TableCell>
                    <TableCell className="text-center py-3 font-semibold text-azul-surelia">{totals.contratos}</TableCell>
                    <TableCell className="text-center py-3 font-semibold text-azul-surelia">{totals.fianzas}</TableCell>
                    <TableCell className="text-right py-3 font-bold text-azul-surelia">
                      {formatCurrency(totals.prima)}
                    </TableCell>
                    <TableCell className="text-right py-3 font-bold text-azul-surelia">
                      {formatCurrency(totals.comision)}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Slide-over panel for fianzas list */}
      {slideOverData && createPortal(
        <div className="fixed inset-y-0 left-0 z-[60] w-72 flex flex-col bg-white shadow-xl border-r border-azul-surelia/20">
          {/* Header */}
          <div className="bg-azul-surelia px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-white truncate">
                  {slideOverData.clientName}
                </h2>
                <p className="text-xs text-white/80">
                  {monthNames[slideOverData.monthIndex].charAt(0).toUpperCase() +
                   monthNames[slideOverData.monthIndex].slice(1)} {selectedYear}
                </p>
              </div>
              <button
                onClick={() => setSlideOverData(null)}
                className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors ml-2"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {(() => {
              const fianzas = getFianzasForClienteMonth(
                slideOverData.clientId,
                slideOverData.monthIndex
              );

              if (fianzas.length === 0) {
                return (
                  <div className="text-center py-8 text-zinc-500 text-sm">
                    No hay fianzas para este período.
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  {fianzas.map((fianza) => {
                    const prima = fianza.primaAmount || 0;
                    const comision = fianza.comisionMonto || 0;

                    return (
                      <div
                        key={fianza.id}
                        onClick={() => setSelectedFianza(fianza)}
                        className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 hover:border-azul-surelia/40 hover:bg-white cursor-pointer transition-all"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-azul-surelia truncate">
                              {formatNumeroFianza(fianza, "Sin número")}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-1 text-xs">
                          <div>
                            <div className="text-zinc-500">Prima</div>
                            <div className="font-semibold text-zinc-900">{formatCurrency(prima)}</div>
                          </div>
                          <div>
                            <div className="text-zinc-500">Tarifa</div>
                            <div className="font-semibold text-zinc-800">
                              {fianza.fianzaTarifa !== null ? `${fianza.fianzaTarifa.toFixed(2)}%` : "N/A"}
                              {fianza.primaMinima && (
                                <span className="ml-1 text-[10px] text-amber-600 font-medium">Prima mín.</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-zinc-500">Comisión</div>
                            <div className="font-semibold text-azul-surelia">
                              {fianza.comisionMonto !== null ? formatCurrency(comision) : "N/A"}
                            </div>
                            {fianza.comisionPct !== null && (
                              <div className="text-[10px] text-azul-surelia/70">
                                {fianza.comisionPct}%
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Summary */}
                  <div className="border-t border-zinc-200 pt-2 mt-3 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-600">{fianzas.length} fianzas</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-600">Prima total</span>
                      <span className="font-semibold text-zinc-900">
                        {formatCurrency(fianzas.reduce((sum, f) => sum + (f.primaAmount || 0), 0))}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-600">Comisión total</span>
                      <span className="font-semibold text-azul-surelia">
                        {formatCurrency(fianzas.reduce((sum, f) => sum + (f.comisionMonto || 0), 0))}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>,
        document.body
      )}

      {/* FianzaView modal */}
      {selectedFianza && createPortal(
        <div className="relative z-[70]">
          <FianzaView
            isOpen={!!selectedFianza}
            onClose={() => setSelectedFianza(null)}
            fianza={selectedFianza}
            clientData={getClientDataForFianza(selectedFianza)}
            contratoData={getContractDataForFianza(selectedFianza)}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
